import React, { useState } from "react";
import {
  Box,
  Typography,
  MenuItem,
  Select,
  Chip,
  IconButton,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import IOSSwitch from "@components/common/Switch";
import {
  type PointInTimeUnit,
  type GuardrailRollingOption,
  type PointInTimeViewerOptions,
  type PointInTimeViewerPeriodConfig,
  type ViewerSelectableTimePeriod,
  GUARDRAIL_ROLLING_OPTION_LABELS,
} from "@models/Dataset";

const PERIOD_LABELS: Record<ViewerSelectableTimePeriod, string> = {
  latest_quarter: "Latest Quarter",
  previous_quarter: "Previous Quarter",
  latest_semester: "Latest Semester",
  previous_semester: "Previous Semester",
};

const PERIOD_TO_UNIT: Record<ViewerSelectableTimePeriod, PointInTimeUnit> = {
  latest_quarter: "quarter",
  previous_quarter: "quarter",
  latest_semester: "semester",
  previous_semester: "semester",
};

interface UnitGuardrailInfo {
  enabled: boolean;
  allowedRollingAverages: GuardrailRollingOption[];
  defaultRollingAverage: GuardrailRollingOption;
}

interface PointInTimeViewerOptionsSectionProps {
  viewerOptions: PointInTimeViewerOptions;
  chartTimePeriod: ViewerSelectableTimePeriod | null;
  chartRollingWindowSize: number;
  pitGuardrailUnits: Partial<Record<PointInTimeUnit, UnitGuardrailInfo>>;
  isDisabled: boolean;
  onViewerOptionsChange: (options: PointInTimeViewerOptions) => void;
}

// ─── Per-period RA row ────────────────────────────────────────────────────────

interface PeriodSectionProps {
  period: ViewerSelectableTimePeriod;
  isChartPeriod: boolean;
  chartRA: GuardrailRollingOption;
  allowedRAs: GuardrailRollingOption[];
  checkedRAs: GuardrailRollingOption[];
  defaultRA?: GuardrailRollingOption;
  isDisabled: boolean;
  onRAToggle: (ra: GuardrailRollingOption, checked: boolean) => void;
  onSetDefault?: (ra: GuardrailRollingOption) => void;
  onRemove?: () => void;
}

const PeriodSection: React.FC<PeriodSectionProps> = ({
  period,
  isChartPeriod,
  chartRA,
  allowedRAs,
  checkedRAs,
  defaultRA,
  isDisabled,
  onRAToggle,
  onSetDefault,
  onRemove,
}) => {
  const [expanded, setExpanded] = useState(true);

  const showRAConfig = allowedRAs.length > 1;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Box
        onClick={() => setExpanded((p) => !p)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 1,
          bgcolor: "grey.50",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {expanded ? (
            <ExpandLessIcon fontSize="small" sx={{ color: "text.secondary" }} />
          ) : (
            <ExpandMoreIcon fontSize="small" sx={{ color: "text.secondary" }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {PERIOD_LABELS[period]}
          </Typography>
          {isChartPeriod && (
            <Chip label="Saved config" size="small" sx={{ fontSize: 10, height: 18 }} />
          )}
        </Box>
        {!isChartPeriod && onRemove && (
          <IconButton
            size="small"
            disabled={isDisabled}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {showRAConfig && (
        <Collapse in={expanded}>
          <Box
            sx={{ px: 1.5, py: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
              {isChartPeriod
                ? "Viewer-selectable rolling average options for this period:"
                : "Rolling average options when viewer selects this period:"}
            </Typography>

            {allowedRAs.map((ra) => {
              const isChartRA = isChartPeriod && ra === chartRA;
              const isChecked = isChartRA || checkedRAs.includes(ra);
              const isDefault = isChartPeriod ? ra === chartRA : ra === defaultRA;

              return (
                <Box
                  key={ra}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={isDisabled || isChartRA}
                      onChange={(e) => onRAToggle(ra, e.target.checked)}
                      style={{ cursor: isChartRA ? "default" : "pointer" }}
                    />
                    <Typography
                      variant="body2"
                      color={isChartRA ? "text.secondary" : "text.primary"}
                    >
                      {GUARDRAIL_ROLLING_OPTION_LABELS[ra]}
                      {isChartRA && " ↑"}
                    </Typography>
                  </Box>

                  {!isChartPeriod && isChecked && onSetDefault && (
                    isDefault ? (
                      <Chip
                        label="Default"
                        size="small"
                        color="primary"
                        sx={{ fontSize: 10, height: 18 }}
                      />
                    ) : (
                      <Typography
                        variant="caption"
                        color="primary"
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => onSetDefault(ra)}
                      >
                        Set default
                      </Typography>
                    )
                  )}
                  {isChartPeriod && isDefault && (
                    <Chip
                      label="Saved config"
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 10, height: 18 }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

// ─── Main section ─────────────────────────────────────────────────────────────

const PointInTimeViewerOptionsSection: React.FC<
  PointInTimeViewerOptionsSectionProps
> = ({
  viewerOptions,
  chartTimePeriod,
  chartRollingWindowSize,
  pitGuardrailUnits,
  isDisabled,
  onViewerOptionsChange,
}) => {
  const [sectionCollapsed, setSectionCollapsed] = useState(false);

  const chartRA = chartRollingWindowSize as GuardrailRollingOption;

  const chartPeriodRAs: GuardrailRollingOption[] = chartTimePeriod
    ? (pitGuardrailUnits[PERIOD_TO_UNIT[chartTimePeriod]]?.allowedRollingAverages ?? [])
    : [];

  const addedAdditionalPeriods = Object.keys(
    viewerOptions.additionalPeriods,
  ) as ViewerSelectableTimePeriod[];

  const allSelectablePeriods: ViewerSelectableTimePeriod[] = [
    "latest_quarter",
    "previous_quarter",
    "latest_semester",
    "previous_semester",
  ];

  const remainingPeriods = allSelectablePeriods.filter((p) => {
    if (p === chartTimePeriod) return false;
    if (addedAdditionalPeriods.includes(p)) return false;
    const unit = PERIOD_TO_UNIT[p];
    return pitGuardrailUnits[unit]?.enabled ?? false;
  });

  const handleToggleEnabled = () => {
    onViewerOptionsChange({ ...viewerOptions, enabled: !viewerOptions.enabled });
  };

  const handleChartPeriodRAToggle = (
    ra: GuardrailRollingOption,
    checked: boolean,
  ) => {
    const current = viewerOptions.chartPeriodAdditionalRollingAverages;
    const updated = checked
      ? ([...current, ra] as GuardrailRollingOption[])
      : current.filter((r) => r !== ra);
    onViewerOptionsChange({
      ...viewerOptions,
      chartPeriodAdditionalRollingAverages: updated,
    });
  };

  const handleAddPeriod = (period: ViewerSelectableTimePeriod) => {
    const unit = PERIOD_TO_UNIT[period];
    const g = pitGuardrailUnits[unit];
    if (!g) return;
    const defaultRA = g.defaultRollingAverage;
    const newConfig: PointInTimeViewerPeriodConfig = {
      allowedRollingAverages: [defaultRA],
      defaultRollingAverage: defaultRA,
    };
    onViewerOptionsChange({
      ...viewerOptions,
      additionalPeriods: { ...viewerOptions.additionalPeriods, [period]: newConfig },
    });
  };

  const handleRemovePeriod = (period: ViewerSelectableTimePeriod) => {
    const { [period]: _removed, ...remaining } = viewerOptions.additionalPeriods;
    onViewerOptionsChange({ ...viewerOptions, additionalPeriods: remaining });
  };

  const handleAdditionalPeriodRAToggle = (
    period: ViewerSelectableTimePeriod,
    ra: GuardrailRollingOption,
    checked: boolean,
  ) => {
    const config = viewerOptions.additionalPeriods[period];
    if (!config) return;
    const current = config.allowedRollingAverages;
    const updated = checked
      ? ([...current, ra] as GuardrailRollingOption[])
      : current.filter((r) => r !== ra);
    const newDefault = updated.includes(config.defaultRollingAverage)
      ? config.defaultRollingAverage
      : (updated[0] ?? ra);
    onViewerOptionsChange({
      ...viewerOptions,
      additionalPeriods: {
        ...viewerOptions.additionalPeriods,
        [period]: { allowedRollingAverages: updated, defaultRollingAverage: newDefault },
      },
    });
  };

  const handleSetDefault = (
    period: ViewerSelectableTimePeriod,
    ra: GuardrailRollingOption,
  ) => {
    const config = viewerOptions.additionalPeriods[period];
    if (!config) return;
    onViewerOptionsChange({
      ...viewerOptions,
      additionalPeriods: {
        ...viewerOptions.additionalPeriods,
        [period]: { ...config, defaultRollingAverage: ra },
      },
    });
  };

  if (!chartTimePeriod) return null;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          mb: sectionCollapsed ? 0 : 2,
        }}
        onClick={() => setSectionCollapsed((p) => !p)}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Viewer Time Options
        </Typography>
        {sectionCollapsed ? (
          <ExpandMoreIcon fontSize="small" sx={{ color: "text.secondary" }} />
        ) : (
          <ExpandLessIcon fontSize="small" sx={{ color: "text.secondary" }} />
        )}
      </Box>

      <Collapse in={!sectionCollapsed}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body2">Enable viewer controls</Typography>
            <IOSSwitch
              checked={viewerOptions.enabled}
              onChange={handleToggleEnabled}
              disabled={isDisabled}
            />
          </Box>

          {viewerOptions.enabled && chartTimePeriod && (
            <>
              <Typography variant="caption" color="text.secondary">
                Configure which named periods and rolling average options viewers
                can select. The chart&apos;s saved config is always included.
              </Typography>

              <PeriodSection
                period={chartTimePeriod}
                isChartPeriod
                chartRA={chartRA}
                allowedRAs={chartPeriodRAs}
                checkedRAs={viewerOptions.chartPeriodAdditionalRollingAverages}
                isDisabled={isDisabled}
                onRAToggle={handleChartPeriodRAToggle}
              />

              {addedAdditionalPeriods.map((period) => {
                const config = viewerOptions.additionalPeriods[period];
                const unit = PERIOD_TO_UNIT[period];
                const g = pitGuardrailUnits[unit];
                if (!config || !g) return null;
                return (
                  <PeriodSection
                    key={period}
                    period={period}
                    isChartPeriod={false}
                    chartRA={chartRA}
                    allowedRAs={g.allowedRollingAverages}
                    checkedRAs={config.allowedRollingAverages}
                    defaultRA={config.defaultRollingAverage}
                    isDisabled={isDisabled}
                    onRAToggle={(ra, checked) =>
                      handleAdditionalPeriodRAToggle(period, ra, checked)
                    }
                    onSetDefault={(ra) => handleSetDefault(period, ra)}
                    onRemove={() => handleRemovePeriod(period)}
                  />
                );
              })}

              {remainingPeriods.length > 0 && (
                <Select
                  displayEmpty
                  value=""
                  size="small"
                  disabled={isDisabled}
                  onChange={(e) =>
                    handleAddPeriod(e.target.value as ViewerSelectableTimePeriod)
                  }
                  renderValue={() => (
                    <Typography variant="body2" color="text.secondary">
                      + Add named period
                    </Typography>
                  )}
                >
                  {remainingPeriods.map((p) => (
                    <MenuItem key={p} value={p}>
                      {PERIOD_LABELS[p]}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </>
          )}

        </Box>
      </Collapse>
    </Box>
  );
};

export default PointInTimeViewerOptionsSection;
